import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Divider,
  Typography,
  alpha,
  useTheme,
  Stack,
  Chip,
  Switch,
} from '@mui/material';
import {
  Refresh,
  Settings,
  EventAvailable as EventAvailableIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import { itemsService, categoriesService, unitsService } from '../../services/items.service';
import { CreateItemDto, Category, Unit, Item } from '../../types/items.types';
import ItemUnitsDialog from './ItemUnitsDialog';
import axios from 'axios';
import config from '../../config';

const translations = {
  en: {
    addItem: 'Add New Item',
    editItem: 'Edit Item',
    itemCode: 'Item Code *',
    itemName: 'Item Name (English) *',
    itemNameAr: 'Item Name (Arabic) *',
    category: 'Category *',
    baseUnit: 'Base Unit *',
    itemType: 'Item Type *',
    barcode: 'Barcode',
    minStock: 'Min Stock Level *',
    maxStock: 'Max Stock Level *',
    hasExpiryDate: 'Track Expiry Date & Batch',
    expiryInfo: 'This item requires expiry date and batch number in invoices',
    product: 'Product',
    service: 'Service',
    rawMaterial: 'Raw Material',
    cancel: 'Cancel',
    save: 'Save',
    loading: 'Loading...',
    generateCode: 'Generate Code',
    manageUnits: 'Manage Units',
    basicInfo: 'Basic Information',
    stockLevels: 'Stock Levels',
    expiryTracking: 'Expiry Tracking',
    active: 'Active',
    inactive: 'Inactive',
    itemStatus: 'Item Status',
    baseUnitLocked: 'Base unit cannot be changed because this item has existing transactions',
  },
  ar: {
    addItem: 'إضافة صنف جديد',
    editItem: 'تعديل صنف',
    itemCode: 'كود الصنف *',
    itemName: 'اسم الصنف (إنجليزي) *',
    itemNameAr: 'اسم الصنف (عربي) *',
    category: 'التصنيف *',
    baseUnit: 'الوحدة الأساسية *',
    itemType: 'نوع الصنف *',
    barcode: 'الباركود',
    minStock: 'الحد الأدنى للمخزون *',
    maxStock: 'الحد الأقصى للمخزون *',
    hasExpiryDate: 'تتبع تاريخ الانتهاء ورقم الدفعة',
    expiryInfo: 'هذا الصنف يتطلب تاريخ انتهاء ورقم دفعة في الفواتير',
    product: 'منتج',
    service: 'خدمة',
    rawMaterial: 'مادة خام',
    cancel: 'إلغاء',
    save: 'حفظ',
    loading: 'جاري التحميل...',
    generateCode: 'توليد الكود',
    manageUnits: 'إدارة الوحدات',
    basicInfo: 'المعلومات الأساسية',
    stockLevels: 'مستويات المخزون',
    expiryTracking: 'تتبع الانتهاء',
    active: 'مفعّل',
    inactive: 'معطّل',
    itemStatus: 'حالة الصنف',
    baseUnitLocked: 'لا يمكن تغيير الوحدة الأساسية لأن هذا الصنف عليه حركات مخزنية',
  },
};

interface ItemDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editItem?: Item | null;
}

const ItemDialog: React.FC<ItemDialogProps> = ({ open, onClose, onSave, editItem }) => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';

  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [showUnitsDialog, setShowUnitsDialog] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [hasTransactions, setHasTransactions] = useState(false);

  const [formData, setFormData] = useState<CreateItemDto & { hasExpiryDate?: boolean }>({
    itemCode: '',
    itemName: '',
    itemNameAr: '',
    categoryId: 0,
    baseUnitId: 0,
    itemType: 'Product',
    barcode: '',
    minStockLevel: 0,
    maxStockLevel: 0,
    hasExpiryDate: false,
  });

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  useEffect(() => {
    if (open && categories.length > 0 && units.length > 0) {
      if (editItem) {
        setFormData({
          itemCode: editItem.itemCode,
          itemName: editItem.itemName,
          itemNameAr: editItem.itemNameAr,
          categoryId: editItem.categoryId,
          baseUnitId: editItem.baseUnitId,
          itemType: editItem.itemType,
          barcode: editItem.barcode || '',
          minStockLevel: editItem.minStockLevel,
          maxStockLevel: editItem.maxStockLevel,
          hasExpiryDate: editItem.hasExpiryDate || false,
        });
        setIsActive(editItem.isActive);
        // ✅ Check if item has transactions to lock BaseUnit
        checkHasTransactions(editItem.itemId);
      } else {
        resetForm();
        setIsActive(true);
        setHasTransactions(false);
      }
    }
  }, [open, editItem, categories, units]);

  const checkHasTransactions = async (itemId: number) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${config.API_BASE_URL}/Items/${itemId}/has-transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasTransactions(res.data);
    } catch {
      setHasTransactions(false);
    }
  };

  const loadData = async () => {
    try {
      const [cats, uns] = await Promise.all([
        categoriesService.getAll(),
        unitsService.getAll(),
      ]);
      setCategories(cats);
      setUnits(uns);
    } catch {
      toast.error('Failed to load item data');
    }
  };

  const handleGenerateCode = async () => {
    try {
      setGenerating(true);
      const newCode = await itemsService.generateCode();
      handleChange('itemCode', newCode);
    } catch {
      toast.error('Failed to generate item code');
    } finally {
      setGenerating(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (editItem) {
        await itemsService.update(editItem.itemId, {
          itemId: editItem.itemId,
          itemName: formData.itemName,
          itemNameAr: formData.itemNameAr,
          categoryId: formData.categoryId,
          baseUnitId: formData.baseUnitId,
          itemType: formData.itemType,
          barcode: formData.barcode || undefined,
          minStockLevel: formData.minStockLevel,
          maxStockLevel: formData.maxStockLevel,
          isActive: isActive,
          hasExpiryDate: formData.hasExpiryDate || false,
        });
      } else {
        await itemsService.create(formData);
      }
      onSave();
      onClose();
      resetForm();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save item');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      itemCode: '',
      itemName: '',
      itemNameAr: '',
      categoryId: 0,
      baseUnitId: 0,
      itemType: 'Product',
      barcode: '',
      minStockLevel: 0,
      maxStockLevel: 0,
      hasExpiryDate: false,
    });
    setIsActive(true);
  };

  const itemTypes = [
    { value: 'Product', label: t.product },
    { value: 'Service', label: t.service },
    { value: 'RawMaterial', label: t.rawMaterial },
  ];

  const selectedCategory = categories.find(c => c.categoryId === formData.categoryId) || null;
  const selectedUnit = units.find(u => u.unitId === formData.baseUnitId) || null;
  const selectedType = itemTypes.find(it => it.value === formData.itemType) || null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        disableRestoreFocus
        keepMounted={false}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #2a2a2a 0%, #1e1e1e 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
          },
        }}
      >
        <DialogTitle
          sx={{
            direction: isRTL ? 'rtl' : 'ltr',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            py: 2,
          }}
        >
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
            {editItem ? t.editItem : t.addItem}
          </Box>
          <Stack direction="row" spacing={1}>
            {editItem && (
              <Tooltip title={t.manageUnits}>
                <IconButton
                  onClick={() => setShowUnitsDialog(true)}
                  sx={{
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.3)',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  <Settings />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={onClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ direction: isRTL ? 'rtl' : 'ltr', mt: 3 }}>

          {/* IsActive Switch - only in edit mode */}
          {editItem && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: isActive
                  ? alpha(theme.palette.success.main, 0.08)
                  : alpha(theme.palette.error.main, 0.08),
                border: '1px solid',
                borderColor: isActive
                  ? alpha(theme.palette.success.main, 0.3)
                  : alpha(theme.palette.error.main, 0.3),
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                {isActive
                  ? <CheckCircleIcon sx={{ color: 'success.main' }} />
                  : <CancelIcon sx={{ color: 'error.main' }} />
                }
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    {t.itemStatus}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isActive ? t.active : t.inactive}
                  </Typography>
                </Box>
              </Stack>
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                color="success"
              />
            </Box>
          )}

          {/* Basic Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
              {t.basicInfo}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label={t.itemCode}
                  value={formData.itemCode}
                  onChange={(e) => handleChange('itemCode', e.target.value)}
                  required
                  disabled={!!editItem}
                  id="item-code"
                />
                {!editItem && (
                  <Tooltip title={t.generateCode}>
                    <IconButton
                      onClick={handleGenerateCode}
                      disabled={generating}
                      color="primary"
                      sx={{
                        border: '1px solid',
                        borderColor: 'primary.main',
                        borderRadius: 1,
                        mt: 1,
                      }}
                    >
                      {generating ? <CircularProgress size={24} /> : <Refresh />}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <TextField
                fullWidth
                label={t.barcode}
                value={formData.barcode}
                onChange={(e) => handleChange('barcode', e.target.value)}
                id="item-barcode"
              />
              <TextField
                fullWidth
                label={t.itemName}
                value={formData.itemName}
                onChange={(e) => handleChange('itemName', e.target.value)}
                required
                id="item-name-en"
              />
              <TextField
                fullWidth
                label={t.itemNameAr}
                value={formData.itemNameAr}
                onChange={(e) => handleChange('itemNameAr', e.target.value)}
                required
                id="item-name-ar"
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mt: 2 }}>
              <Autocomplete
                options={categories}
                getOptionLabel={(option) => isRTL ? option.categoryNameAr : option.categoryName}
                value={selectedCategory}
                onChange={(_, newValue) => handleChange('categoryId', newValue?.categoryId || 0)}
                renderInput={(params) => (
                  <TextField {...params} label={t.category} required id="item-category" />
                )}
                isOptionEqualToValue={(option, value) => option.categoryId === value.categoryId}
              />
              <Autocomplete
                options={units}
                getOptionLabel={(option) => isRTL ? option.unitNameAr : option.unitName}
                value={selectedUnit}
                onChange={(_, newValue) => handleChange('baseUnitId', newValue?.unitId || 0)}
                disabled={!!editItem && hasTransactions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t.baseUnit}
                    required
                    id="item-unit"
                    helperText={editItem && hasTransactions ? t.baseUnitLocked : undefined}
                    error={editItem && hasTransactions ? false : undefined}
                    FormHelperTextProps={{
                      sx: editItem && hasTransactions ? { color: 'warning.main', fontWeight: 600 } : undefined
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.unitId === value.unitId}
              />
              <Autocomplete
                options={itemTypes}
                getOptionLabel={(option) => option.label}
                value={selectedType}
                onChange={(_, newValue) => handleChange('itemType', newValue?.value || 'Product')}
                renderInput={(params) => (
                  <TextField {...params} label={t.itemType} required id="item-type" />
                )}
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />
            </Box>
          </Box>

          {/* Stock Levels */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
              {t.stockLevels}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              <TextField
                fullWidth
                type="number"
                label={t.minStock}
                value={formData.minStockLevel}
                onChange={(e) => handleChange('minStockLevel', Number(e.target.value))}
                required
                id="item-min-stock"
                inputProps={{ min: 0, step: 0.001 }}
              />
              <TextField
                fullWidth
                type="number"
                label={t.maxStock}
                value={formData.maxStockLevel}
                onChange={(e) => handleChange('maxStockLevel', Number(e.target.value))}
                required
                id="item-max-stock"
                inputProps={{ min: 0, step: 0.001 }}
              />
            </Box>
          </Box>

          {/* Expiry Tracking */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              background: formData.hasExpiryDate
                ? alpha(theme.palette.warning.main, 0.1)
                : alpha(theme.palette.grey[500], 0.05),
              border: '1px solid',
              borderColor: formData.hasExpiryDate
                ? alpha(theme.palette.warning.main, 0.3)
                : alpha(theme.palette.grey[500], 0.1),
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
              {t.expiryTracking}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.hasExpiryDate || false}
                  onChange={(e) => handleChange('hasExpiryDate', e.target.checked)}
                  color="warning"
                  icon={<EventAvailableIcon />}
                  checkedIcon={<EventAvailableIcon />}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {t.hasExpiryDate}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.expiryInfo}
                  </Typography>
                </Box>
              }
            />

            {formData.hasExpiryDate && (
              <Chip
                label={language === 'ar' ? 'مفعّل' : 'Enabled'}
                color="warning"
                size="small"
                icon={<EventAvailableIcon />}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ direction: isRTL ? 'rtl' : 'ltr', p: 3, gap: 1 }}>
          <Button onClick={onClose} disabled={loading} variant="outlined">
            {t.cancel}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              px: 4,
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : t.save}
          </Button>
        </DialogActions>
      </Dialog>

      {editItem && (
        <ItemUnitsDialog
          open={showUnitsDialog}
          onClose={() => setShowUnitsDialog(false)}
          itemId={editItem.itemId}
          itemName={isRTL ? editItem.itemNameAr : editItem.itemName}
        />
      )}
    </>
  );
};

export default ItemDialog;